import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, DataCard, StatusPill } from "@/components/admin/AdminPrimitives";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/admin/logs")({
  head: () => ({ meta: [{ title: "Logs — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: LogsAdmin,
});

function LogsAdmin() {
  const list = useQuery({
    queryKey: ["admin.logs"],
    queryFn: async () => {
      const [events, audit] = await Promise.all([
        supabase.from("webhook_events").select("*").order("created_at", { ascending: false }).limit(100),
        supabase.from("audit_logs").select("*").order("created_at", { ascending: false }).limit(100),
      ]);
      return { events: events.data ?? [], audit: audit.data ?? [] };
    },
  });

  return (
    <div>
      <PageHeader title="Logs" description="Auditoria completa da plataforma." />
      <div className="grid gap-4 lg:grid-cols-2">
        <DataCard>
          <div className="border-b border-black/[0.05] px-5 py-3">
            <p className="font-display text-sm font-extrabold text-[var(--navy)]">Webhooks</p>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {list.isLoading ? (
              <div className="space-y-1 p-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
            ) : list.data?.events.length === 0 ? (
              <p className="py-12 text-center text-sm text-[var(--gray-ink)]">Sem eventos.</p>
            ) : (
              <ul className="divide-y divide-black/[0.05]">
                {(list.data?.events ?? []).map((e: any) => (
                  <li key={e.id} className="flex items-start gap-2 px-4 py-2.5">
                    <StatusPill tone={e.error ? "red" : e.processed_at ? "green" : "gold"}>
                      {e.error ? "Falha" : e.processed_at ? "OK" : "Pendente"}
                    </StatusPill>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-[var(--navy)]">
                        {e.source} · {e.event_type ?? "—"}
                      </p>
                      <p className="truncate text-[11px] text-[var(--gray-ink)]">{e.error ?? e.external_id ?? "—"}</p>
                    </div>
                    <span className="shrink-0 text-[10px] text-[var(--gray-ink)]">
                      {new Date(e.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DataCard>

        <DataCard>
          <div className="border-b border-black/[0.05] px-5 py-3">
            <p className="font-display text-sm font-extrabold text-[var(--navy)]">Auditoria</p>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {list.isLoading ? (
              <div className="space-y-1 p-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
            ) : (list.data?.audit ?? []).length === 0 ? (
              <p className="py-12 text-center text-sm text-[var(--gray-ink)]">Sem registros.</p>
            ) : (
              <ul className="divide-y divide-black/[0.05]">
                {(list.data?.audit ?? []).map((a: any) => (
                  <li key={a.id} className="px-4 py-2.5">
                    <p className="text-sm font-medium text-[var(--navy)]">{a.action}</p>
                    <p className="truncate text-[11px] text-[var(--gray-ink)]">
                      {(a.entity ?? a.entity_type) ?? "—"} · {a.entity_id ?? "—"}
                    </p>
                    <span className="text-[10px] text-[var(--gray-ink)]">
                      {new Date(a.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DataCard>
      </div>
    </div>
  );
}
